import { Component, HostListener, Injector } from '@angular/core';
import { appModuleAnimation } from '@shared/animations/routerTransition';
import { PagedCommonResultRequestDto, PagedListingComponentBase } from '@shared/paged-listing-component-base';
import { ReminderDto, ReminderDtoPagedResultDto, ReminderServiceProxy, UserTablePreferenceDto, UserTablePreferenceServiceProxy } from '@shared/service-proxies/service-proxies';
import { finalize, Subscription } from "rxjs";
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CustomModalService } from '@shared/helpers/custom-modal.service';
import { CreateOrEditReminderComponent } from '../create-or-edit-reminder/create-or-edit-reminder.component';
import * as moment from 'moment';
import { DatePipe } from '@node_modules/@angular/common';
import { DomSanitizer, SafeHtml } from '@node_modules/@angular/platform-browser';
import truncate from 'html-truncate';
import { ReminderService } from '@app/reminders/reminder.service';
import { ElementRef, Renderer2 } from '@angular/core';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-reminders-diary',
  templateUrl: './reminders-diary.component.html',
  styleUrl: './reminders-diary.component.css',
  animations: [appModuleAnimation()],
})
export class RemindersDiaryComponent extends PagedListingComponentBase<ReminderDto> {

  icon: string = "pending_actions";
  title: string = this.l("Reminders");

  reminderList: ReminderDto[] = [];
  loading = false;
  keyword: string = '';
  showingFilters = false;
  orderField: string = '';
  orderDirection: number = 1;
  isResizing = false;
  showColumnSettings = false;

  private subs = new Subscription();

  columns = [
    { field: 'nextExecution', header: this.l('NextExecutionDate'), visible: true },
    { field: 'recipient', header: this.l('Remind'), visible: true },
    { field: 'linkedTo', header: this.l('LinkedTo'), visible: true },
    { field: 'type', header: this.l('Type'), visible: true },
    { field: 'priority', header: this.l('Priority'), visible: true },
    { field: 'description', header: this.l('Description'), visible: true },
    { field: 'state', header: this.l('State'), visible: true },
    { field: 'creatorUserName', header: this.l('CreatorUser'), visible: false },
    { field: 'creationTime', header: this.l('CreationTime'), visible: false },
  ];

  reminderFilters = {
    recipientIds: [] as number[],
    typeIds: [] as number[],
    priorityIds: [] as number[],
    stateValues: [] as string[],
    creationDateRange: [null, null] as [Date | null, Date | null],
    creatorUserIds: [] as number[]
  };

  recipientOptions: { id: number; name: string }[] = [];
  typeOptions: { id: number; name: string }[] = [];
  priorityOptions: { id: number; name: string }[] = [];
  stateOptions: { label: string; value: string }[] = [];

  executionDateRange: Date[] = [];
  minDate: Date = new Date("2000-01-01");
  maxDate: Date = new Date("2100-12-31");
  creatorUserOptions: { id: number; name: string }[] = [];

  showEntityDropdown: boolean = false;
  searchTerm: string = '';
  groupedEntities: any[] = [];

  selectedEntityIds: number[] = [];
  filteredEntities = [];


  constructor(
    injector: Injector,
    private _reminderService: ReminderServiceProxy,
    private _modalService: CustomModalService,
    private userPrefService: UserTablePreferenceServiceProxy,
    private _datePipe: DatePipe,
    private sanitizer: DomSanitizer,
    public _reminderServiceParam: ReminderService,
    private _elementRef: ElementRef,
    private renderer: Renderer2
  ) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.subs.add(
      this._reminderServiceParam.refresh$.subscribe(() => {
        this.refresh();
      })
    );

    this.loadFilterOptions();
    this.loadGroupedEntities();

    this.userPrefService.get('reminders').subscribe(pref => {
      if (!pref) return;
      if (pref.sortColumn) {
        this.orderField = pref.sortColumn;
        this.orderDirection = pref.sortDirection === 0 ? -1 : 1; // Desc: -1, Asc: 1
      }
      try {
        const parsed = JSON.parse(pref.columnPreferences ?? '');
        if (Array.isArray(parsed)) {
          parsed.sort((a, b) => a.order - b.order);
          const allFields = this.columns.map(c => c.field);
          this.columns = allFields.map(field => {
            const saved = parsed.find(p => p.field === field);
            const original = this.columns.find(c => c.field === field);
            return {
              field,
              header: original?.header ?? field,
              visible: saved?.visible ?? original?.visible ?? false
            };
          });
        }
      } catch (err) {
      }
      this.getDataPage({
        first: 0,
        rows: this.pageSize,
        sortField: this.orderField,
        sortOrder: this.orderDirection
      });
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  loadFilterOptions(): void {
    this._reminderService.getReminderTypeOptions().subscribe(types => {
      this.typeOptions = types;
    });

    this._reminderService.getReminderPriorityOptions().subscribe(priorities => {
      this.priorityOptions = [
        { id: -1, name: "(" + this.l("Empty") + ")" },
        ...priorities
      ];
    });

    this._reminderService.getRecipientOptions().subscribe(recipients => {
      this.recipientOptions = recipients;
    });

    this._reminderService.getAllCreatorUsersForReminders().subscribe(res => {
      this.creatorUserOptions = res.items.map(u => ({
        id: u.value,
        name: u.name
      }));
    });

    this.stateOptions = [
      { label: this.l("InProgress"), value: "InProgress" },
      { label: this.l("Paused"), value: "Paused" },
      { label: this.l("Completed"), value: "Completed" },
    ];
  }

  loadGroupedEntities(): void {
    forkJoin({
      companies: this._reminderService.getLinkedCompanies(),
      juridicalPersons: this._reminderService.getLinkedJuridicalPersons(),
      naturalPersons: this._reminderService.getLinkedNaturalPersons(),
    }).subscribe(({ companies, juridicalPersons, naturalPersons }) => {
      this.groupedEntities = [
        {
          label: this.l("Companies"),
          expanded: false,
          items: companies.map(c => ({ ...c, type: 'Company' }))
        },
        {
          label: this.l("JuridicalPersons"),
          expanded: false,
          items: juridicalPersons.map(j => ({ ...j, type: 'JuridicalPerson' }))
        },
        {
          label: this.l("NaturalPersons"),
          expanded: false,
          items: naturalPersons.map(n => ({ ...n, type: 'NaturalPerson' }))
        }
      ];
      this.filteredEntities = [...this.groupedEntities];
    });
  }

  get visibleColumns() {
    return this.columns.filter(c => c.visible);
  }

  isEntitySelected(id: number): boolean {
    return this.selectedEntityIds.includes(id);
  }

  onSearchTermChange(): void {
    const term = this.searchTerm?.toLowerCase() ?? '';

    if (!term) {
      this.filteredEntities = this.groupedEntities.map(g => ({
        ...g,
        items: [...g.items],
        expanded: true,
      }));
      return;
    }

    this.filteredEntities = this.groupedEntities
      .map(group => {
        const filteredItems = group.items.filter(item =>
          item.name.toLowerCase().includes(term)
        );

        return {
          ...group,
          items: filteredItems,
          expanded: true
        };
      })
      .filter(group => group.items.length > 0);
  }

  toggleEntitySelection(id: number): void {
    const index = this.selectedEntityIds.indexOf(id);
    if (index === -1) {
      this.selectedEntityIds.push(id);
    } else {
      this.selectedEntityIds.splice(index, 1);
    }
    this.onFilterChange();
  }

  toggleGroup(group: any): void {
    group.expanded = !group.expanded;
  }

  getSelectedEntitiesLabel(): string {
    const selectedNames: string[] = [];

    for (const group of this.groupedEntities) {
      for (const item of group.items) {
        if (this.selectedEntityIds.includes(item.id)) {
          selectedNames.push(item.name);
        }
      }
    }

    if (selectedNames.length === 0) return '';

    if (selectedNames.length <= 2) {
      return selectedNames.join(', ');
    } else {
      return this.l("ItemsSelected{0}", selectedNames.length);
    }
  }

  isGroupFullySelected(group: any): boolean {
    if (!group.items?.length) return false;
    return group.items.every(item => this.selectedEntityIds.includes(item.id));
  }

  toggleGroupSelection(group: any, selectAll: boolean): void {
    if (!group.items?.length) return;
    const itemIds = group.items.map(i => i.id);
    if (selectAll) {
      itemIds.forEach(id => {
        if (!this.selectedEntityIds.includes(id)) {
          this.selectedEntityIds.push(id);
        }
      });
    } else {
      this.selectedEntityIds = this.selectedEntityIds.filter(id => !itemIds.includes(id));
    }
    this.onFilterChange();
  }

  toggleColumn(col): void {
    const visibleCount = this.columns.filter(c => c.visible).length;
    if (col.visible && visibleCount === 1) {
      abp.message.warn(this.l("AtLeastOneColumnVisible"));
      return;
    }
    col.visible = !col.visible;
    this.applyResizeListeners();

    // Force column redistribution immediately
    setTimeout(() => {
      this.redistributeColumnWidths();
    }, 50);
  }

  redistributeColumnWidths(): void {
    const table = document.querySelector('.p-datatable-table') as HTMLElement;
    if (!table) return;

    const tableId = table.id;
    if (!tableId) return;

    const headerCells = table.querySelectorAll('thead th') as NodeListOf<HTMLElement>;
    const visibleHeaders = Array.from(headerCells).filter(th =>
      th.style.display !== 'none' && !th.classList.contains('aux-col') && !th.classList.contains('actions-col')
    );

    if (visibleHeaders.length === 0) return;

    const auxColWidth = 4;
    const actionsColWidth = 8;
    const reservedPercentage = auxColWidth + actionsColWidth;
    const availablePercentage = 100 - reservedPercentage;
    const equalWidthPercent = availablePercentage / visibleHeaders.length;

    let styleElement = document.getElementById('primeng-column-redistribution') as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'primeng-column-redistribution';
      document.head.appendChild(styleElement);
    }

    let cssRules = `
      #${tableId} {
        table-layout: fixed !important;
        width: 100% !important;
      }
    `;

    visibleHeaders.forEach((header) => {
      const columnIndex = Array.from(headerCells).indexOf(header) + 1;

      cssRules += `
        #${tableId} > .p-datatable-thead > tr > th:nth-child(${columnIndex}),
        #${tableId} > .p-datatable-tbody > tr > td:nth-child(${columnIndex}),
        #${tableId} > .p-datatable-tfoot > tr > td:nth-child(${columnIndex}) {
          width: ${equalWidthPercent}% !important;
          max-width: ${equalWidthPercent}% !important;
          min-width: ${equalWidthPercent}% !important;
        }
      `;
    });

    styleElement.textContent = cssRules;
  }

  showFilters(): void {
    this.showingFilters = !this.showingFilters;
  }

  truncateHtmlWithFormat(html: string, maxLength: number): SafeHtml {
    const cleanedHtml = html
      ?.replace(/&nbsp;/g, ' ')
      ?.replace(/&amp;/g, '&')
      ?.replace(/&lt;/g, '<')
      ?.replace(/&gt;/g, '>')
      ?.replace(/&quot;/g, '"')
      ?.replace(/&#39;/g, "'");

    const truncated = truncate(cleanedHtml || '', maxLength, { ellipsis: '' });
    return this.sanitizer.bypassSecurityTrustHtml(truncated);
  }

  onSort(event: any): void {
    this.orderField = event.sortField;
    this.orderDirection = event.sortOrder;

    this.getDataPage({
      first: (this.pageNumber - 1) * this.pageSize,
      rows: this.pageSize,
      sortField: this.orderField,
      sortOrder: this.orderDirection
    });
  }

  onColumnDrop(event: any): void {
    const visibleCols = this.visibleColumns;
    const draggedCol = visibleCols[event.previousIndex];
    const targetCol = visibleCols[event.currentIndex];

    const prevRealIndex = this.columns.findIndex(c => c.field === draggedCol?.field);
    const currRealIndex = this.columns.findIndex(c => c.field === targetCol?.field);

    if (prevRealIndex !== -1 && currRealIndex !== -1 && prevRealIndex !== currRealIndex) {
      const movedColumn = this.columns.splice(prevRealIndex, 1)[0];
      this.columns.splice(currRealIndex, 0, movedColumn);
    }
  }

  onSearch(): void {
    this.pageNumber = 1;
    this.getDataPage({
      first: 0,
      rows: this.pageSize,
      sortField: this.orderField,
      sortOrder: this.orderDirection
    });
  }

  getOrderDirectionEnum(sortOrder: number): number {
    return sortOrder === 1 ? 1 : 0;
  }

  getDataPage(event: any): void {
    if (event.first == undefined) {
      event = {
        first: (this.pageNumber - 1) * this.pageSize,
        rows: this.pageSize,
        sortField: this.orderField,
        sortOrder: this.orderDirection
      };
    }

    this.prepareFilters();
    this.pageNumber = event.first / event.rows + 1;

    this.orderField = event.sortField ?? this.orderField;
    this.orderDirection = event.sortOrder ?? this.orderDirection;

    const request = {
      skipCount: event.first || 0,
      maxResultCount: event.rows,
      keyword: this.keyword || ''
    };

    this.loading = true;
    this.list(request, this.pageNumber, () => {
      this.loading = false;
    });
  }

  applyResizeListeners(): void {
    setTimeout(() => {
      const resizers = document.querySelectorAll('.p-column-resizer');
      let tooltipEl = document.getElementById('custom-resize-tooltip') as HTMLElement;
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'custom-resize-tooltip';
        tooltipEl.classList.add('custom-tooltip');
        tooltipEl.textContent = this.l('DragToResize');
        document.body.appendChild(tooltipEl);
      }

      resizers.forEach((el: HTMLElement) => {
        el.onmouseenter = () => {
          this.isResizing = true;
          const rect = el.getBoundingClientRect();
          tooltipEl.style.top = rect.top - 30 + 'px';
          tooltipEl.style.left = rect.left + 'px';
          tooltipEl.classList.add('visible');
        };

        el.onmouseleave = () => {
          this.isResizing = false;
          tooltipEl.classList.remove('visible');
        };
      });
    }, 0);
  }

  saveUserPreferences(): void {
    const prefs = UserTablePreferenceDto.fromJS({
      columnPreferences: JSON.stringify(
        this.columns.map((col, index) => ({
          field: col.field,
          visible: col.visible,
          order: index
        }))
      ),
      sortColumn: this.orderField,
      sortDirection: this.getOrderDirectionEnum(this.orderDirection)
    });

    this.userPrefService.save('reminders', prefs).subscribe(() => {
      this.notify.success(this.l('SavedSuccessfully'));
    });
  }

  createItem(): void {
    this._modalService.show(
      CreateOrEditReminderComponent,
      {
        class: 'modal-xm',
        closeInterceptor: () => Promise.reject(),
        initialState: {
          title: this.l('CreateReminder'),
        },
      },
      'createReminderundefined'
    );
  }

  onFilterChange(): void {
    this.prepareFilters();
    this.getDataPage({
      first: 0,
      rows: this.pageSize,
      sortField: this.orderField,
      sortOrder: this.orderDirection
    });
  }

  onDateRangeSelected(range: Date[]): void {
    this.executionDateRange = range;
    this.onFilterChange();
  }

  onDateRangeEnter(): void {
    if (!this.executionDateRange || !this.executionDateRange[0] || !this.executionDateRange[1]) {
      this.executionDateRange = [];
    }
    this.onFilterChange();
  }

  prepareFilters(): void {
    const filters: any[] = [];

    if (this.reminderFilters.recipientIds?.length) {
      filters.push({
        fieldName: 'RecipientIds',
        value: this.reminderFilters.recipientIds.join(','),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    if (this.reminderFilters.typeIds?.length) {
      filters.push({
        fieldName: 'ReminderTypeIds',
        value: this.reminderFilters.typeIds.join(','),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    if (this.reminderFilters.priorityIds?.length) {
      const ids = [...this.reminderFilters.priorityIds];
      const hasEmpty = ids.includes(-1);
      const realIds = ids.filter(id => id !== -1);

      filters.push({
        fieldName: 'ReminderPriorityIds',
        value: realIds.join(','),
        fieldType: 1,
        fieldComparator: 0
      });

      if (hasEmpty) {
        filters.push({
          fieldName: 'HasNoPriority',
          value: 'true',
          fieldType: 1,
          fieldComparator: 0
        });
      }
    }

    if (this.reminderFilters.stateValues?.length) {
      filters.push({
        fieldName: 'States',
        value: this.reminderFilters.stateValues.join(','),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    if (this.executionDateRange?.length === 2) {
      filters.push({
        fieldName: 'DateFrom',
        value: this.executionDateRange[0].toISOString(),
        fieldType: 1,
        fieldComparator: 0
      });
      filters.push({
        fieldName: 'DateTo',
        value: this.executionDateRange[1].toISOString(),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    if (this.reminderFilters.creatorUserIds?.length) {
      filters.push({
        fieldName: 'CreatorUserIds',
        value: this.reminderFilters.creatorUserIds.join(','),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    if (this.reminderFilters.creationDateRange?.length === 2 && this.reminderFilters.creationDateRange[0] && this.reminderFilters.creationDateRange[1]) {
      const from = new Date(this.reminderFilters.creationDateRange[0]);
      from.setHours(0, 0, 0, 0);
      const to = new Date(this.reminderFilters.creationDateRange[1]);
      to.setHours(23, 59, 59, 999);

      filters.push({
        fieldName: 'CreationFrom',
        value: from.toISOString(),
        fieldType: 1,
        fieldComparator: 0
      });

      filters.push({
        fieldName: 'CreationTo',
        value: to.toISOString(),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    if (this.selectedEntityIds?.length) {
      filters.push({
        fieldName: 'LinkedEntityIds',
        value: this.selectedEntityIds.join(','),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    filters.push({
      fieldName: 'VisibleColumns',
      value: this.visibleColumns.map(c => c.field).join(','),
      fieldType: 1,
      fieldComparator: 0
    });

    this.filters = filters;
  }

  protected list(request: PagedCommonResultRequestDto, pageNumber: number, finishedCallback: Function): void {
    this._reminderService
      .getAll(
        this.orderField,
        this.getOrderDirectionEnum(this.orderDirection),
        request.keyword,
        this.filters,
        request.skipCount,
        request.maxResultCount
      )
      .pipe(
        finalize(() => {
          finishedCallback();
        })
      )
      .subscribe((result: ReminderDtoPagedResultDto) => {
        this.reminderList = result.items;
        this.showPaging(result, pageNumber);
        this.applyResizeListeners();
      });
  }

  protected delete(entity: ReminderDto): void {
    abp.message.confirm(
      this.l("AreYouSureWantToDelete{0}", entity.reminderType.name),
      undefined,
      (result: boolean) => {
        if (result) {
          this._reminderService.delete(entity.id).subscribe({
            next: () => {
              abp.notify.success(this.l("SuccessfullyDeleted"));

              this.getDataPage({
                first: (this.pageNumber - 1) * this.pageSize,
                rows: this.pageSize,
                sortField: this.orderField,
                sortOrder: this.orderDirection
              });
            },
            error: () => {
              this.getDataPage({
                first: (this.pageNumber - 1) * this.pageSize,
                rows: this.pageSize,
                sortField: this.orderField,
                sortOrder: this.orderDirection
              });
            }
          });
        } else {
          this.getDataPage({
            first: (this.pageNumber - 1) * this.pageSize,
            rows: this.pageSize,
            sortField: this.orderField,
            sortOrder: this.orderDirection
          });
        }
      }
    );
  }

  getRecipientsText(reminder: ReminderDto): string {
    return reminder.reminderRecipients
      .map(r => r.user.fullName)
      .join(' | ');
  }

  editItem(item: ReminderDto): void {
    let createOrEditItemDialog: BsModalRef;
    createOrEditItemDialog = this._modalService.show(
      CreateOrEditReminderComponent,
      {
        class: 'modal-xm',
        closeInterceptor: () => Promise.reject(),
        initialState: {
          reminderId: item.id,
        },
      },
      'createReminder' + item.id
    );

    if (createOrEditItemDialog) {
      createOrEditItemDialog.onHide.subscribe(() => {
        this.getDataPage({
          first: (this.pageNumber - 1) * this.pageSize,
          rows: this.pageSize,
          sortField: this.orderField,
          sortOrder: this.orderDirection
        });
      });
    }
  }

  getNotificationState(item: ReminderDto) {
    if (item.paused) {
      return this.l("Paused");
    }

    const next = this.nextExecutionDate(item);
    if (!next) {
      return this.l("Completed");
    }
    return this.l("InProgress");
  }

  nextExecutionDate(item: ReminderDto): moment.Moment | null {
    if (!item.executionDates?.length) {
      return null;
    }

    const currentUtcTime = moment().utc();

    let utcOffsetString = (item.utc ?? "0").trim().toUpperCase();
    if (utcOffsetString.startsWith("UTC")) {
      utcOffsetString = utcOffsetString.substring(3);
    }

    const utcOffsetMatch = utcOffsetString.match(/^([+-]?)(\d{1,2})(?::(\d{2}))?$/);
    let timezoneOffsetMinutes = 0;
    if (utcOffsetMatch) {
      const offsetSign = utcOffsetMatch[1] === "-" ? -1 : 1;
      const offsetHours = parseInt(utcOffsetMatch[2], 10);
      const offsetMinutes = utcOffsetMatch[3] ? parseInt(utcOffsetMatch[3], 10) : 0;
      timezoneOffsetMinutes = offsetSign * (offsetHours * 60 + offsetMinutes);
    }

    const reminderTime = moment(item.time);
    const targetHour = reminderTime.hour();
    const targetMinute = reminderTime.minute();
    const targetSecond = reminderTime.second();
    const daysBeforeExecution = item.daysBefore ?? 0;

    const chronologicalExecutionDates = [...item.executionDates].sort((a, b) =>
      moment.utc(a).valueOf() - moment.utc(b).valueOf()
    );

    for (const executionDate of chronologicalExecutionDates) {
      const scheduledDateWithTime = moment.utc(executionDate)
        .set({ hour: targetHour, minute: targetMinute, second: targetSecond });

      const actualScheduledTimeUtc = scheduledDateWithTime.clone().subtract(timezoneOffsetMinutes, 'minutes');
      const reminderTimeUtc = actualScheduledTimeUtc.clone().subtract(daysBeforeExecution, 'days');

      if (reminderTimeUtc.isAfter(currentUtcTime)) {
        return reminderTimeUtc;
      }

      if (actualScheduledTimeUtc.isAfter(currentUtcTime)) {
        return actualScheduledTimeUtc;
      }
    }

    return null;
  }

  getNextExecution(item: ReminderDto) {
    var nextExecutionDate = this.nextExecutionDate(item);

    if (nextExecutionDate == undefined && item.lastExecutionDate != undefined)
      return this._datePipe.transform(item.lastExecutionDate.toDate()) + " " + item.time.utc(false).format('HH:mm');

    if (nextExecutionDate == undefined && item.lastExecutionDate == undefined)
      return this._datePipe.transform(moment().toDate()) + " " + item.time.utc(false).format('HH:mm');

    return this._datePipe.transform(nextExecutionDate.toDate()) + " " + item.time.utc(false).format('HH:mm');
  }

  isNextExecutionUndefined(item: ReminderDto) {
    var nextExecutionDate = this.nextExecutionDate(item);

    if (nextExecutionDate == undefined) {
      return true;
    }
    else {
      return false;
    }
  }

  formatTooltipWithLineBreaks(text: string): string {
    if (!text) return '';
    let htmlWithBreaks = text
      .replace(/<\/p>\s*<p>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]+>/g, '');
    htmlWithBreaks = htmlWithBreaks.replace(/&nbsp;/g, ' ');
    return htmlWithBreaks.trim();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this._elementRef.nativeElement.querySelector('.custom-dropdown-container')
      ?.contains(event.target as Node);

    if (!clickedInside && this.showEntityDropdown) {
      this.showEntityDropdown = false;
    }
  }

}
